import { useContext } from 'react';
import { StatusLabel } from '@shared/components';
import { TERMINOLOGY } from '@shared/i18n';
import { SignInContext } from '@modules/users';
import type { TFolder } from '@modules/items';
import { AccessType } from '@modules/access/types';
import { useRegistrationDetails } from '../api';
import { isRegistrationFolder, isRegistrationsEnabled } from '../helpers/registrationAccess';
import RegistrationDetailsForm from './RegistrationDetailsForm';

type TDetailsProps = {
  folderId: string;
};

const RegistrationDetails = ({ folderId }: TDetailsProps) => {
  const { data, error } = useRegistrationDetails(folderId);

  // A legacy registration folder has no details. They are never created from here.
  if (data === null) return null;

  return (
    <StatusLabel
      data={data}
      error={error}
      loadingMessage={`Loading ${TERMINOLOGY.registrationSingle.lower} details...`}
      errorMessage={`Loading ${TERMINOLOGY.registrationSingle.lower} details failed!`}
      render={(details) => <RegistrationDetailsForm key={details.registrationFolder} details={details} />}
    />
  );
};

type TProps = {
  folder: TFolder;
};

// Entry point on the folder page. Nothing is fetched unless the feature is on, the folder is a registration folder
// and the user can read it: partial access only lists the folder's children.
const RegistrationDetailsSection = ({ folder }: TProps) => {
  const { organization } = useContext(SignInContext);

  if (
    !isRegistrationsEnabled(organization) ||
    !isRegistrationFolder(folder, organization) ||
    folder.accessRight < AccessType.READ
  )
    return null;

  return <RegistrationDetails folderId={folder._id} />;
};

export default RegistrationDetailsSection;
